(() => {
  'use strict';
  const root = window.Shopify?.routes?.root || '/';
  const initial = document.querySelector('[data-one-dollar-state]');
  if (!initial) return;
  let state = JSON.parse(initial.textContent);
  let pending = 0;
  let queue = Promise.resolve();
  let refreshVersion = 0;
  let checking = false;
  let error = '';
  let verificationFailed = false;
  const limitMessage = 'Only one $1 item is allowed. Remove your current offer item from the cart before choosing another.';
  const checkoutSelector = '[name="checkout"], a[href="/checkout"], a[href$="/checkout"]';

  function message() {
    if (state.quantity > 1) return limitMessage;
    const minimum = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: state.currency || 'USD' }).format(state.minimum / 100);
    return `Your $1 item requires another product outside the offer collection and a cart total of at least ${minimum} after discounts, before shipping. Add more products or remove the $1 item to check out.`;
  }

  function paint() {
    document.querySelectorAll('[data-one-dollar-product]').forEach(marker => {
      const form = marker.form;
      if (!form) return;
      const variantId = form.elements.namedItem('id')?.value;
      const available = marker.dataset.availableVariants.split(',').includes(String(variantId));
      const disabled = pending > 0 || checking || verificationFailed || state.quantity > 0 || !available;
      // form.elements includes sticky add buttons associated via the form attribute.
      Array.from(form.elements).filter(control => control.type === 'submit').forEach(button => {
        if (button.disabled !== disabled) button.disabled = disabled;
      });
    });
    document.querySelectorAll('[data-one-dollar-add]').forEach(button => {
      const disabled = pending > 0 || checking || verificationFailed || state.quantity > 0 || button.dataset.available !== 'true';
      if (button.disabled !== disabled) button.disabled = disabled;
      const label = button.dataset.available !== 'true' ? 'Sold out' : state.quantity > 0 ? 'Product already added' : pending ? 'Adding…' : 'Add to cart';
      if (button.textContent !== label) button.textContent = label;
    });
    document.querySelectorAll('[data-one-dollar-selection]').forEach(node => {
      const text = error || (state.quantity > 0 ? limitMessage : '');
      if (node.textContent !== text) node.textContent = text;
    });
    document.querySelectorAll(checkoutSelector).forEach(button => {
      if (state.blocked) {
        if (!button.hasAttribute('data-offer-requirement')) button.setAttribute('data-offer-requirement', '');
        const label = button.querySelector?.('[data-offer-checkout-label]');
        const minimum = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: state.currency || 'USD', currencyDisplay: 'code' }).format(state.minimum / 100);
        const text = state.quantity > 1 ? 'Only one $1 item allowed' : `Minimum order ${minimum} required`;
        if (label && label.textContent !== text) label.textContent = text;
      } else if (button.hasAttribute('data-offer-requirement')) {
        button.removeAttribute('data-offer-requirement');
      }
      const locked = state.blocked || pending > 0 || checking || verificationFailed;
      if (locked) {
        if (!button.hasAttribute('data-offer-locked')) {
          button.dataset.offerLocked = button.disabled ? 'disabled' : 'enabled';
        }
        if (button.getAttribute('aria-disabled') !== 'true') button.setAttribute('aria-disabled', 'true');
        if ('disabled' in button && !button.disabled) button.disabled = true;
      } else if (!locked && button.hasAttribute('data-offer-locked')) {
        if ('disabled' in button) button.disabled = button.dataset.offerLocked === 'disabled';
        button.removeAttribute('aria-disabled');
        button.removeAttribute('data-offer-locked');
      }
    });
    document.querySelectorAll('[data-one-dollar-notice]').forEach(node => {
      node.hidden = !verificationFailed;
      const text = verificationFailed ? error : message();
      if (node.textContent !== text) node.textContent = text;
    });
    document.querySelectorAll('.additional-checkout-buttons, .shopify-payment-button').forEach(node => {
      const hide = state.quantity > 0 || state.productIsOffer || pending > 0 || checking || verificationFailed;
      if (hide && !node.hasAttribute('data-offer-hidden')) {
        node.dataset.offerHidden = node.style.display;
        node.style.display = 'none';
      } else if (!hide && node.hasAttribute('data-offer-hidden')) {
        node.style.display = node.dataset.offerHidden;
        delete node.dataset.offerHidden;
      }
    });
  }

  async function readState(path = `${root}cart`) {
    const url = new URL(path, location.origin);
    url.searchParams.set('section_id', 'one-dollar-state');
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error('Unable to check the offer. Please refresh and try again.');
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const data = html.querySelector('[data-one-dollar-state]');
    if (!data) throw new Error('Unable to check the offer. Please refresh and try again.');
    return JSON.parse(data.textContent);
  }

  async function refresh() {
    const version = ++refreshVersion;
    checking = true;
    paint();
    try {
      const fresh = await readState();
      if (version === refreshVersion) {
        state = { ...fresh, productIsOffer: state.productIsOffer };
        error = '';
        verificationFailed = false;
      }
    } catch (cause) {
      if (version === refreshVersion) { error = cause.message; verificationFailed = true; }
    } finally {
      if (version === refreshVersion) { checking = false; paint(); }
    }
  }

  // Serialize theme product-form additions so rapid clicks cannot reserve two offer items.
  function add(url, options, offerProduct = false) {
    pending++;
    paint();
    const operation = queue.then(async () => {
      try {
        const form = options.body;
        const variantId = form.get('id');
        const fresh = await readState(`${root}variants/${encodeURIComponent(variantId)}`);
        state = { ...fresh, productIsOffer: state.productIsOffer };
        const quantity = Number(form.get('quantity') || 1);
        if ((offerProduct || fresh.productIsOffer) && (fresh.quantity > 0 || quantity !== 1)) {
          return new Response(JSON.stringify({ status: 422, description: limitMessage }), { status: 422, headers: { 'Content-Type': 'application/json' } });
        }
        const response = await fetch(url, options);
        await refresh();
        return response;
      } catch (cause) {
        error = cause.message;
        verificationFailed = true;
        return new Response(JSON.stringify({ status: 422, description: error }), { status: 422, headers: { 'Content-Type': 'application/json' } });
      } finally {
        pending--;
        paint();
      }
    });
    queue = operation.catch(() => {});
    return operation;
  }

  window.OneDollarOffer = { add };

  document.addEventListener('submit', async event => {
    const target = event.target;
    const form = target.matches('form') && target.querySelector('[data-one-dollar-product]') ? target : target.closest('[data-one-dollar-form]');
    if (!form) return;
    event.preventDefault();
    // Own offer submissions before the theme's product-form handler can send a second request.
    event.stopImmediatePropagation();
    if (pending || checking || state.quantity > 0) return;
    error = '';
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
    const body = new FormData(form);
    body.set('sections', sections.join(','));
    body.set('sections_url', location.pathname);
    const response = await add(`${root}cart/add.js`, { method: 'POST', body }, true);
    try {
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Unable to add this item.');
      const cartResponse = await fetch(`${root}cart.js`, { cache: 'no-store' });
      if (!cartResponse.ok) throw new Error('Item added. Please refresh to view your cart.');
      const cart = await cartResponse.json();
      cart.sections = result.sections;
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
      form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: [result], cart } }));
    } catch (cause) { error = cause.message; }
    paint();
  }, true);

  // Validate again on checkout, including when another browser tab changed the cart.
  async function checkout(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    await queue;
    await refresh();
    if (state.blocked || verificationFailed) { paint(); return; }
    location.assign(`${root}checkout`);
  }
  document.addEventListener('click', event => {
    const control = event.target.closest(checkoutSelector);
    if (control) return checkout(event);
  }, true);
  document.addEventListener('submit', event => {
    if (event.submitter?.matches(checkoutSelector) || (event.target.action?.endsWith('/cart') && event.target.querySelector('[name="checkout"]'))) return checkout(event);
  }, true);
  document.addEventListener('cart:change', refresh);
  document.addEventListener('cart:refresh', refresh);
  window.addEventListener('pageshow', refresh);
  window.addEventListener('focus', refresh);
  new MutationObserver(paint).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
  paint();
})();
